import { SignaturePicker } from "@/components/feature/Settings/signatures/SignaturePicker"
import { Typography } from "@/components/ui/typography"
import { useGetAllFeatureFlags } from "@/contexts/FeatureFlagContext"
import { SETTINGS_PATHS } from "@/features/settings/paths"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useOrgSignatures } from "@/services/api/organizationSignaturesApi"
import { Settings } from "lucide-react"
import { useEffect, useState } from "react"
import { Link } from "react-router"
import { toastApiError } from "../../../../api/apiError"
import {
  useUpdateConformitySignature,
  type ConformityDetail,
} from "../../../../api/conformityApi"
import { Field } from "./Field"

/**
 * Signature picker for the conformity editor. The CALLER'S OWN saved
 * signatures are always visible (one click, not opt-in-then-pick) — a
 * signature image is personal, not shared org branding, so `useOrgSignatures`
 * never surfaces a colleague's rows even though the endpoint is
 * `/organization/signatures`. Clicking one PATCHes `signatureId` on the
 * draft; clicking the already-applied one does nothing, so a repeated click
 * after a slow refresh cannot unsign by accident. Unsigning is the explicit
 * "Remove signature" action. The preview panel's query is
 * invalidated as part of that mutation, so the server re-renders and the
 * iframe updates on its own — the ruled wet-sign line is already handled in
 * Python (conformity_renderer's `.sigrow`), so there's no client-side
 * compositing here and the preview can't diverge from the issued PDF.
 *
 * There is no default signature: nothing is pre-selected on a fresh draft,
 * and the unsigned state is spelled out under the cards instead.
 *
 * Known gap: if a declaration was signed by a DIFFERENT user (their signature
 * still renders correctly in the preview/PDF), this picker shows none of its
 * cards as selected and offers no way to unsign it from here — accepted for
 * now, see the design doc.
 */
export function ConformitySignatureField({
  detail,
  detailIsFetching,
  onSignatureChanged,
}: {
  detail: ConformityDetail
  /** A successful PATCH invalidates the declaration detail query (this is
   *  where `signatureId` — and so `selected` below — actually comes from),
   *  not just the mutation itself. Clearing the clicked card's spinner the
   *  moment the PATCH resolves left a visible gap where it looked idle but
   *  the checkmark hadn't moved yet. Kept pending through that refetch too. */
  detailIsFetching: boolean
  /** Fired once the PATCH itself resolves (success or clear) — lets the
   *  parent arm a one-shot "scroll the preview to the signature block" for
   *  whenever the invalidated preview-html query's refetch actually lands,
   *  which happens on its own timing, later than this call. Receives the
   *  signature id that is now applied, null when the signature was removed,
   *  so the parent can tell the two directions apart. */
  onSignatureChanged?: (signatureId: string | null) => void
}) {
  const { translate } = useTranslation()
  const flags = useGetAllFeatureFlags()
  const { data: signatures, isLoading: signaturesLoading } = useOrgSignatures(
    flags.ENABLE_ORG_SIGNATURES,
  )
  const updateSignature = useUpdateConformitySignature()

  const applied = detail.signatureId

  // A draft can carry a signatureId the org has since soft-deleted from
  // Settings — the picker's own list excludes deleted rows (only the render
  // path reads past soft-delete, deliberately, so an already-issued PDF keeps
  // its signature), so `applied` would otherwise match no rendered card and
  // offer no way back to unsigned. Only meaningful once the live list has
  // actually loaded — before that, "not found yet" isn't "deleted".
  const appliedSignatureMissing =
    !!applied &&
    !signaturesLoading &&
    !(signatures ?? []).some((signature) => signature.id === applied)

  // Which change is in flight, as `SignaturePicker`'s `pendingId` reads it:
  // the clicked card's id, or `null` for a removal (the picker's Remove and
  // the "no longer in your library" Clear are the same PATCH). Tracked here
  // rather than read off the mutation's `isPending`/`variables` because it
  // has to outlive the PATCH, see below. `null` state = nothing in flight.
  const [pendingChange, setPendingChange] = useState<{
    id: string | null
  } | null>(null)

  // Cleared once BOTH the mutation and the detail refetch it triggered have
  // settled — on a failed PATCH detailIsFetching never turns true for this
  // attempt, so this still clears promptly.
  useEffect(() => {
    if (pendingChange && !updateSignature.isPending && !detailIsFetching) {
      setPendingChange(null)
    }
  }, [pendingChange, updateSignature.isPending, detailIsFetching])

  if (!flags.ENABLE_ORG_SIGNATURES) return null

  async function handleClick(id: string) {
    // One mutation at a time: while a change is in flight the picker
    // disables every card and Remove (only the clicked one spins); this
    // guard is the backstop, so a click that still gets through is a silent
    // no-op instead of a second, possibly-out-of-order PATCH.
    if (updateSignature.isPending || applied === id) return
    setPendingChange({ id })
    try {
      await updateSignature.mutateAsync({ id: detail.id, signatureId: id })
      onSignatureChanged?.(id)
    } catch (err) {
      toastApiError(err, translate, "MC_CS_CONF_EDIT_SIGN_TOAST_ERR")
    }
  }

  async function handleClear() {
    if (updateSignature.isPending) return
    setPendingChange({ id: null })
    try {
      await updateSignature.mutateAsync({ id: detail.id, signatureId: null })
      onSignatureChanged?.(null)
    } catch (err) {
      toastApiError(err, translate, "MC_CS_CONF_EDIT_SIGN_TOAST_ERR")
    }
  }

  // `SignaturePicker`'s onChange is id-based, one callback for both a card
  // click and its own "Remove" affordance — `null` is always a clear.
  function handlePickerChange(id: string | null) {
    if (id === null) {
      void handleClear()
    } else {
      void handleClick(id)
    }
  }

  return (
    <Field
      label={translate("MC_CS_CONF_EDIT_SIGN_SECTION")}
      right={
        <Link
          to={SETTINGS_PATHS.signatures}
          className="flex items-center gap-1 text-[11px] font-normal normal-case tracking-normal text-grey5 hover:text-white"
        >
          <Settings className="h-3 w-3" />
          {translate("MC_CS_CONF_EDIT_SIGN_MANAGE_LINK")}
        </Link>
      }
    >
      {appliedSignatureMissing && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-md border border-grey4 bg-grey2 px-2.5 py-2">
          <Typography as="muted" className="text-[11px] text-grey5">
            {translate("MC_CS_CONF_EDIT_SIGN_MISSING")}
          </Typography>
          <button
            type="button"
            onClick={() => void handleClear()}
            disabled={pendingChange !== null}
            className="shrink-0 text-[11px] font-medium text-grey5 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {translate("MC_CS_CONF_EDIT_SIGN_CLEAR")}
          </button>
        </div>
      )}
      <div className="rounded-md border border-grey4 bg-grey2 p-2.5">
        {/* The card grid, empty/loading states and the remove affordance all
            live in the shared picker now (design §5) — this field keeps only
            what's specific to a declaration's applied signature: the
            "no longer in your library" notice above, and owning the PATCH
            mutation / pending-through-refetch timing below. */}
        <SignaturePicker
          value={applied}
          onChange={handlePickerChange}
          pending={pendingChange !== null}
          pendingId={pendingChange?.id}
        />
      </div>
    </Field>
  )
}
